import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

// Shared nerdle labs infrastructure (think-aloud methodology, reusable across games).
import { AdLayout } from '../../shared/components/AdLayout'
import { BrowserGate } from '../../shared/components/BrowserGate'
import { Brand } from '../../shared/components/Brand'
import { ReflectionNotice } from '../../shared/components/ReflectionNotice'
import { StatusScreen } from '../../shared/components/StatusScreen'
import { OtherGamesLinks } from '../../shared/components/OtherGamesLinks'
import { labsHomeUrl } from '../../shared/labs/labsNav'
import { hasConsented, markConsented } from '../../shared/labs/progress'
import { browserGateReason, captureEnv, enrichCountry, CaptureEnv, isInAppWebView } from '../../shared/lib/env'
import { uuidv4 } from '../../shared/lib/uuid'
import { useSpeechRecognition, TranscriptEntry } from '../../shared/labs/useSpeechRecognition'
import { useStreamingTranscription } from '../../shared/labs/useStreamingTranscription'
import { getSttEngine } from '../../shared/labs/sttEngine'
import { getRetroAfter } from '../../shared/labs/retroConfig'
import { getSourceTag } from '../../shared/labs/sourceTag'
import { getNerdleSkill } from '../../shared/labs/nerdleSkill'
import { BrowserHandoff } from '../../shared/labs/BrowserHandoff'
import { PublishConsent } from '../../shared/labs/PublishConsent'
import { getRevealAssignment, revealRegimeFor } from '../../shared/labs/revealArm'
import { hasSeenPuzzle, markPuzzleSeen, seenPuzzleKeys } from '../../shared/labs/seenPuzzles'
import { useThinkingEnergy, ENERGY_CONFIG } from '../../shared/labs/useThinkingEnergy'
import { InteractionEvent, PuzzleTrace, PuzzleRetro, CaptureMode, StopRule, TRACE_SCHEMA_VERSION, RETRO_SCHEMA_VERSION } from '../../shared/labs/types'
import { saveConsent, savePuzzleTrace, savePuzzleRetro, savePanelSignup } from '../../shared/labs/sessionStore'
import { readNerdleIdentity } from '../../shared/labs/panelAuth'
import { ConfirmedSegment, formatWithPauses } from '../../shared/labs/transcript'

// 24-game-specific UI + logic.
import { ConsentScreen } from './components/ConsentScreen'
import { PuzzleView } from './components/PuzzleView'
import { PuzzleStage } from './components/PuzzleStage'
import { AnswerKeypad } from './components/AnswerKeypad'
import { SummaryScreen } from './components/SummaryScreen'
import { PanelSignup } from './components/PanelSignup'
import { RetroScreen, RetroAnswer } from './components/RetroScreen'
import { SurveyScreen } from '../../shared/labs/SurveyScreen'
import { labsFeedbackSurvey, surveyCompletedAt, markSurveyDone } from '../../shared/labs/survey'
import { track, setLabsUser } from '../../shared/labs/analytics'
import { logFunnel, errDetail, installWindowErrorLogger, FunnelContext } from '../../shared/labs/funnel'
import { LABS_BUILD } from '../../shared/labs/build'
import { addStreamedMs, overDailyCap } from '../../shared/labs/streamLimit'
import {
  practiceSession,
  buildTestSession,
  buildSubgoalSession,
  buildDivisionSession,
  pickPlayPuzzle,
  buildExtensionPuzzles,
  assertPoolSolvable,
  SessionPuzzle,
  Difficulty,
} from './game24/puzzles'
import { exampleSolution } from './game24/solver'
import { judgeGame24 } from './game24/evaluate'
import { prettyExpr } from './game24/format'
import { scoreTranscript, MatchResult } from './game24/spokenMatch'

const REFLECTION_SECONDS = 5
const STT_ENGINE = getSttEngine()
const RETRO = getRetroAfter() // retrospective ("aha") follow-up regime (see retroConfig)
const SOURCE = getSourceTag() // acquisition attribution: ?src=email&t=<token> (see sourceTag)
// Reveal A/B: the assignment is recorded here, but arm B is NOT implemented in this game yet,
// so revealRegimeFor returns 'A' until '24' joins REVEAL_B_LIVE. That is the whole point of
// carrying arm and regime separately - a B-assigned player here experiences A, visibly.
const SKILL = getNerdleSkill() // device-local participant covariates; read once, like SOURCE
// Read once, like SKILL. Inside a native app WebView the mic is unavailable and on iOS
// merely CALLING getUserMedia kills the app, so this gates the auto-start effect below.
const IN_APP = isInAppWebView()
const REVEAL = getRevealAssignment(SOURCE.token)
const REGIME = revealRegimeFor('24', REVEAL.arm)
const EXTENSION_PUZZLES = 5 // opt-in "keep going" research puzzles after the core 10

// Phase-2 arm allocation. When enabled, a test session is randomly assigned to one of three arms
// at entry. Full-set is the invariant control (Wurgaft replication + drift monitor); subgoal and
// division are the contrast arms. Weights are relative (need not sum to 1); full-set is held at its
// share and only subgoal/division are rebalanced if one lags. Off until go-live.
type Arm = 'full-set' | 'subgoal' | 'division'
const PHASE2_ENABLED = true
// 2026-10-15. PHASE 2 COLLECTION CLOSED; back to the single full-set arm the game ran before
// Phase 2. Not a deviation: PREREG_phase2 section 6 runs the allocation "until each primary
// outcome resolves against its registered margin", and section 8 puts the full-set replication
// outside that register entirely as a Phase-1 continuation.
//   DV1 +15.1pp [11.1, 19.1], half-width 4.0 against a registered 7  -> DETECTED
//   DV2 52.4% [47.8, 56.8] (keyword proxy), SG0 +9.5pp [2.6, 16.5]   -> DETECTED
//   SG1 is the only open outcome and the register says it needs the coded-graph re-run on the
//   278 trials already held, not more data. Same reasoning that stopped subgoal on 3 September.
// Logged as a closure in PREREG_phase2 section 9, because section 6 requires any cap or early
// stop to be recorded. Reversible in one line; restarting either contrast arm is a decision
// needing its own entry there.
// Why now rather than at the words target: full-set buys +1.6 trials per session [+0.9, +2.2] and
// +19.9pp completion [+10.9, +28.9] on the arm that is like-for-like with W24, out of a pool that
// does not replenish. Words per session is a wash, +37.5 [-36.6, +110.8], because division trials
// are wordier (65.6 against 56.0), so this is about yield per participant and NOT about words.
// 2026-09-03 rebalance, for the record: { 'full-set': 1, subgoal: 0, division: 2 }
const ARM_WEIGHTS: Record<Arm, number> = { 'full-set': 1, subgoal: 0, division: 0 }
const pickArm = (): Arm => {
  const arms = Object.entries(ARM_WEIGHTS) as [Arm, number][]
  const total = arms.reduce((s, [, w]) => s + w, 0)
  let r = Math.random() * total
  for (const [a, w] of arms) if ((r -= w) < 0) return a
  return 'full-set'
}
const buildArmSession = (a: Arm): { conditionId: number; puzzles: SessionPuzzle[] } =>
  a === 'subgoal' ? buildSubgoalSession() : a === 'division' ? buildDivisionSession() : buildTestSession()

// Dev-only QA override: ?arm=subgoal|division|full-set forces the arm so each can be walked through
// deterministically in development. Never active in a production build.
const ARM_OVERRIDE: Arm | null = (() => {
  if (process.env.NODE_ENV === 'production') return null
  try {
    const a = new URLSearchParams(window.location.search).get('arm')
    return a === 'subgoal' || a === 'division' || a === 'full-set' ? a : null
  } catch {
    return null
  }
})()
const PUZZLE_LIMIT_MS = 3 * 60 * 1000 // Wurgaft's 3-minute per-puzzle limit → auto-end unsolved
const NUDGE_SILENCE_MS = 3000

// ---- The FORCED-STOP arm -----------------------------------------------------------------
// Our give-up control is the largest departure from Wurgaft's protocol, and nothing yet measures
// what it buys in participation: failing is known to cause leaving (the harder arm completes
// 32.9% of sessions against 51.5%), and participants take the control on 46.4% of failed trials,
// but the counterfactual has never been run. This arm is that counterfactual.
//
// It changes ONE thing. The Pass control is hidden and a failed trial stays open to the 3-minute
// limit; the one-commit rule is untouched, so this does not confound the stopping rule with the
// retry rule. A detected correct answer still ends the trial early and scores correct, which is
// also what Wurgaft's protocol does on a solve: only UNSOLVED trials run the full window there.
//
// LIVE from 2026-10-16, build '2026-10-16-forcedarm-live'. Registered in
// analysis/human24/PREREG_stoprule.md (REGISTERED 2026-10-16, amended A1 the same day), walked
// through in QA_stoprule.md across nine cases, approved by RM. Setting this back to false stops
// assigning and is the kill switch; it does not need a registration entry, but stopping the arm
// early does, under section 8.
const FORCED_ARM_LIVE = true
// 80% FORCED, 20% free. The forced arm is the scarce side: the free protocol is already
// banked at 248 full-set sessions plus every historical session, so the concurrent free arm
// is a drift check rather than a matched control, and what cannot be bought anywhere else is
// speech from past the point a participant wanted to stop.
const FORCED_SHARE = 0.8
const pickStopRule = (): StopRule =>
  FORCED_ARM_LIVE && Math.random() < FORCED_SHARE ? 'forced' : 'free'

// Dev-only QA override, mirroring ARM_OVERRIDE: ?stop=forced walks the held window through
// deterministically. Never active in a production build.
const STOPRULE_OVERRIDE: StopRule | null = (() => {
  if (process.env.NODE_ENV === 'production') return null
  try {
    const s = new URLSearchParams(window.location.search).get('stop')
    return s === 'forced' || s === 'free' ? s : null
  } catch {
    return null
  }
})()
const COMMIT_WORD = /\bcommit\b/i

// localStorage: has the player completed the 10-game test (which unlocks free play)?
const TEST_DONE_KEY = 'labs24_test_complete'
const isTestDone = (): boolean => {
  try {
    return localStorage.getItem(TEST_DONE_KEY) === '1'
  } catch {
    return false
  }
}
const markTestDone = () => {
  try {
    localStorage.setItem(TEST_DONE_KEY, '1')
  } catch {
    /* noop */
  }
}

const verdictNote = (v: ReturnType<typeof judgeGame24> | null, numbers: number[]): string | null => {
  if (!v || v.correct) return null
  switch (v.reason) {
    case 'wrong-numbers':
      return `You need to use all four numbers — ${numbers.join(', ')} — each exactly once.`
    case 'wrong-value':
      return v.value !== null ? `That comes to ${v.value}, not 24.` : 'That doesn’t come to 24.'
    case 'parse-error':
      return 'Couldn’t read that — check the brackets and operators.'
    case 'empty':
      return 'Looks like nothing was entered.'
    default:
      return null
  }
}

type AnswerMode = 'voice-detected' | 'typed' | 'pass' | 'timeout'

type Phase =
  | 'consent'
  | 'mic-error'
  | 'daily-limit' // 60-min/day streamed-audio cap reached (see streamLimit)
  | 'mode-choice' // returning (unlocked) player: free play or retake the test
  | 'practice-intro'
  | 'test-intro'
  | 'play-intro' // difficulty selector for free play
  | 'playing'
  | 'reflection'
  | 'detected'
  | 'entry'
  | 'forced' // forced-arm only: held on a failed trial until the 3-minute limit
  | 'verdict'
  | 'retro' // retrospective ("aha") follow-up after a solved puzzle
  | 'continue-offer' // opt-in to a few more research puzzles after the core test
  | 'summary'
  | 'publish-consent' // optional opt-in to publishing this session's transcripts
  | 'survey' // end-of-play feedback survey
  | 'panel' // re-engagement sign-up (logged-out players, after feedback)

function Game24() {
  const gateReason = useMemo(() => browserGateReason(), [])

  const [phase, setPhase] = useState<Phase>('consent')
  // Re-engagement panel: capture logged-in players on their FIRST test puzzle (see
  // finishAdvance — engagement, not completion, so partial players are included too);
  // route logged-out players through the sign-up card once, after the feedback step.
  const panelCapturedRef = useRef(false)
  const panelDoneRef = useRef(false)
  const isLoggedIn = () => {
    try {
      return !!localStorage.getItem('lbl_token')
    } catch {
      return false
    }
  }
  const afterFeedback = () => setPhase(!isLoggedIn() && !panelDoneRef.current ? 'panel' : 'play-intro')
  const [confirmedSegments, setConfirmedSegments] = useState<ConfirmedSegment[]>([])
  const [interimText, setInterimText] = useState('')
  const [tsBase, setTsBase] = useState(0)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [autoMatch, setAutoMatch] = useState<MatchResult | null>(null)
  const [typedResult, setTypedResult] = useState<ReturnType<typeof judgeGame24> | null>(null)
  const [retroSpoken, setRetroSpoken] = useState('') // live spoken retro answer (finals)
  const [retroInterim, setRetroInterim] = useState('')
  const [showPassConfirm, setShowPassConfirm] = useState(false) // "keep going vs give up" modal
  const [retroCommitSignal, setRetroCommitSignal] = useState(0) // bumped when "commit" heard during retro

  // ---- mode/session state ----
  const [mode, setMode] = useState<CaptureMode>('practice')
  const [conditionId, setConditionId] = useState<number | null>(null)
  const [arm, setArm] = useState<Arm | null>(null) // Phase-2 arm for the current test session
  // Forced-stop arm, drawn once per SESSION at consent rather than at test entry, so the Pass
  // control is absent from the first puzzle a participant ever sees. RM, 2026-10-16: "We can't
  // show someone a pass button on training and then take it away!" Drawing it later made the
  // control appear during the two practice puzzles and vanish at test start, which is a behaviour
  // change mid-session and a reactivity risk in itself. The cost is that assignment now precedes
  // practice, so practice dropout is differential by arm: see the registration's
  // practice-to-test conversion outcome, which exists to measure exactly that.
  const [stopRule, setStopRule] = useState<StopRule | null>(null)
  // The same value as a ref, because `beginTest` needs it synchronously for its funnel + track
  // calls and a state setter from `handleStart` has not necessarily flushed by then.
  const stopRuleRef = useRef<StopRule | null>(null)
  // The outcome the forced window is holding open. A held trial records the answerMode that WOULD
  // have ended it, so the three-way failure split stays comparable with the free arm; only a
  // detection inside the window changes it.
  const pendingRef = useRef<{ mode: AnswerMode; typed: ReturnType<typeof judgeGame24> | null; raw: string | null } | null>(null)
  const forcedStartRef = useRef<number>(0)
  const forcedEndRef = useRef<number>(0)
  const [playDifficulty, setPlayDifficulty] = useState<Difficulty | undefined>(undefined)
  const [sessionPuzzles, setSessionPuzzles] = useState<SessionPuzzle[]>([])
  const [puzzleIndex, setPuzzleIndex] = useState(0)
  const puzzle = sessionPuzzles[puzzleIndex] as SessionPuzzle | undefined
  const solutionExample = useMemo(() => (puzzle ? exampleSolution(puzzle.numbers) : null), [puzzle])
  const isLastInSession = puzzleIndex >= sessionPuzzles.length - 1
  const progressLabel =
    mode === 'test'
      ? `Puzzle ${puzzleIndex + 1} of ${sessionPuzzles.length}`
      : mode === 'practice'
      ? `Warm-up ${puzzleIndex + 1} of ${sessionPuzzles.length}`
      : `Free play · puzzle ${puzzleIndex + 1}`

  // ---- refs (per-puzzle capture buffers) ----
  const sessionRef = useRef<{ id: string; startedAtISO: string } | null>(null)
  const envRef = useRef<CaptureEnv | null>(null)
  const transcriptRef = useRef<TranscriptEntry[]>([])
  const eventsRef = useRef<InteractionEvent[]>([])
  const puzzleStartRef = useRef<{ iso: string; perf: number } | null>(null)
  const committedTranscriptRef = useRef<string>('')
  const hasCommittedRef = useRef(false)
  const committedAtRef = useRef<number>(0)
  const reflectionEndRef = useRef<number>(0)
  const dimRef = useRef(false)
  const sampleCounterRef = useRef(0)
  const lastActivityRef = useRef(0)
  const autoRejectedRef = useRef(false)
  const lastTypedRef = useRef<string | null>(null)
  const tsBaseRef = useRef(0)
  const sessionTracesRef = useRef<PuzzleTrace[]>([]) // completed traces in the current mode session
  const retroTranscriptRef = useRef<TranscriptEntry[]>([]) // spoken retro answer, captured separately
  const retroPromptedAtRef = useRef<number>(0)
  const retroPromptedISORef = useRef<string>('')
  const coreLenRef = useRef(0) // length of the core test set (before any extension)
  const extendedRef = useRef(false) // player opted into the extra research puzzles
  const endUnsolvedRef = useRef<(reason: 'pass' | 'timeout') => void>(() => {}) // latest, for the timer
  const endForcedWindowRef = useRef<(detected: boolean) => void>(() => {}) // latest, for the timer

  // Context stamped on every funnel event. Read from refs at call time (not captured), so it
  // is correct even when fired from a window-level error listener installed once at mount.
  const funnelCtx = useCallback(
    (): FunnelContext => ({
      sessionId: sessionRef.current?.id ?? null,
      game: 'twentyfour',
      revealArm: REVEAL.arm,
      env: envRef.current,
      source: SOURCE,
    }),
    []
  )

  // Fires only after React has COMMITTED the first test puzzle, so its absence means the
  // puzzle never reached the screen (daily-cap bounce, or a render throw) rather than the
  // participant giving up on it. Reset in beginTest so a retaken test logs again.
  const firstTestPuzzleLoggedRef = useRef(false)
  useEffect(() => {
    if (phase !== 'playing' || mode !== 'test' || puzzleIndex !== 0) return
    if (firstTestPuzzleLoggedRef.current) return
    firstTestPuzzleLoggedRef.current = true
    logFunnel('test-puzzle-shown', funnelCtx(), { trialIndex: 0 })
  }, [phase, mode, puzzleIndex, funnelCtx])

  const pushEvent = useCallback((e: InteractionEvent) => {
    eventsRef.current.push(e)
  }, [])

  useEffect(() => {
    assertPoolSolvable()
    track('labs_consent_view')
  }, [])

  // Wurgaft's 3-minute per-puzzle limit: while solving, arm a timer for the time remaining
  // in this puzzle; if it fires the trial ends unsolved (reason 'timeout'). Committing
  // leaves 'playing', which clears it via cleanup, so it can't fire after an answer.
  useEffect(() => {
    // 'forced' is included deliberately: the held window is still inside the 3-minute limit,
    // and the effect already computes the time REMAINING from the puzzle start, so it re-arms
    // correctly across the commit rather than needing its own clock.
    if (phase !== 'playing' && phase !== 'forced') return
    const start = puzzleStartRef.current?.perf ?? performance.now()
    const fireIfOver = () => {
      if (performance.now() - start < PUZZLE_LIMIT_MS) return
      // A held window has already committed, and `endUnsolved` returns early on that guard, so
      // the cap has to be routed to the window's own ending or it would never fire.
      if (phase === 'forced') endForcedWindowRef.current(false)
      else endUnsolvedRef.current('timeout')
    }
    const remaining = Math.max(0, PUZZLE_LIMIT_MS - (performance.now() - start))
    const id = window.setTimeout(fireIfOver, remaining)
    // A backgrounded/frozen tab (esp. mobile) suspends the timer while performance.now()
    // keeps counting, so an abandoned puzzle would otherwise record hours. Re-check on wake
    // so an overrun the sleeping timer missed ends immediately instead of running on.
    const onVisible = () => {
      if (document.visibilityState === 'visible') fireIfOver()
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      window.clearTimeout(id)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [phase, puzzleIndex])

  // ---- thinking-energy mechanic ----
  const energy = useThinkingEnergy({
    running: phase === 'playing' || phase === 'reflection',
    tsBase,
    resetKey: `${mode}-${puzzleIndex}`,
    onSample: (s) => {
      const nowDim = s.energy <= ENERGY_CONFIG.dimThreshold
      if (nowDim !== dimRef.current) {
        dimRef.current = nowDim
        pushEvent({ ts: s.ts, type: nowDim ? 'dim_on' : 'dim_off' })
      }
      sampleCounterRef.current += 1
      if (sampleCounterRef.current % Math.max(1, Math.round(1000 / ENERGY_CONFIG.tickMs)) === 0) {
        pushEvent({ ts: s.ts, type: 'energy_sample', energy: s.energy, speaking: s.speaking })
      }
    },
  })

  // Commit the answer (button OR spoken "commit"). Guarded to fire once per puzzle.
  const handleCommit = useCallback(() => {
    if (hasCommittedRef.current) return
    hasCommittedRef.current = true
    const segments: ConfirmedSegment[] = transcriptRef.current
      .filter((t) => t.isFinal)
      .map((t) => ({ text: t.text.trim(), ts: t.ts }))
      .filter((s) => s.text)
    committedTranscriptRef.current = formatWithPauses(segments)
    committedAtRef.current = performance.now()
    pushEvent({ ts: Math.round(performance.now() - tsBase), type: 'commit', value: committedTranscriptRef.current })
    pushEvent({ ts: Math.round(performance.now() - tsBase), type: 'reflection_start' })
    setPhase('reflection')
  }, [tsBase, pushEvent])

  // ---- transcription (both engines share these handlers; only one is active) ----
  const onTranscript = (entry: TranscriptEntry) => {
    if (phase === 'retro') {
      // Route retro speech into its own buffer so it never contaminates the puzzle trace.
      if (entry.isFinal) {
        retroTranscriptRef.current.push(entry)
        const t = entry.text.trim()
        if (t) setRetroSpoken((prev) => (prev ? `${prev} ${t}` : t))
        setRetroInterim('')
        // Same commit gesture as solving: saying "commit" submits the retro answer.
        if (COMMIT_WORD.test(entry.text)) setRetroCommitSignal((n) => n + 1)
      } else {
        setRetroInterim(entry.text)
      }
      return
    }
    transcriptRef.current.push(entry)
    energy.registerActivity()
    lastActivityRef.current = performance.now()
    if (entry.isFinal) {
      const text = entry.text.trim()
      if (text) setConfirmedSegments((prev) => [...prev, { text, ts: entry.ts }])
      setInterimText('')
    } else {
      setInterimText(entry.text)
    }
    if (phase === 'playing' && COMMIT_WORD.test(entry.text)) handleCommit()
  }
  const onSpeechStart = () => {
    energy.registerActivity()
    lastActivityRef.current = performance.now()
    pushEvent({ ts: Math.round(performance.now() - tsBase), type: 'speech_start' })
  }
  const onSpeechEnd = () => pushEvent({ ts: Math.round(performance.now() - tsBase), type: 'speech_end' })
  const onPermissionDenied = () => setPhase('mic-error')

  // 'forced' belongs here or the held window records a 180-second silence: the recogniser is
  // gated on this and is otherwise off from the answer screen onwards.
  const speechActive =
    phase === 'playing' || phase === 'reflection' || phase === 'retro' || phase === 'forced'
  useSpeechRecognition({
    active: speechActive && STT_ENGINE === 'webspeech',
    tsBase,
    onTranscript,
    onSpeechStart,
    onSpeechEnd,
    onPermissionDenied,
  })
  const streaming = useStreamingTranscription({
    active: speechActive && STT_ENGINE !== 'webspeech',
    tsBase,
    provider: STT_ENGINE === 'google' ? 'google' : 'deepgram',
    onTranscript,
    onSpeechStart,
    onSpeechEnd,
    onPermissionDenied,
  })

  // ---- per-puzzle lifecycle ----
  const startPuzzle = useCallback(
    (index: number) => {
      if (overDailyCap()) {
        setPhase('daily-limit')
        return
      }
      transcriptRef.current = []
      eventsRef.current = []
      committedTranscriptRef.current = ''
      hasCommittedRef.current = false
      pendingRef.current = null
      forcedStartRef.current = 0
      forcedEndRef.current = 0
      autoRejectedRef.current = false
      lastTypedRef.current = null
      dimRef.current = false
      sampleCounterRef.current = 0
      setConfirmedSegments([])
      setInterimText('')
      setAutoMatch(null)
      setTypedResult(null)
      setShowPassConfirm(false)
      puzzleStartRef.current = { iso: new Date().toISOString(), perf: performance.now() }
      lastActivityRef.current = performance.now()
      pushEvent({ ts: Math.round(performance.now() - tsBaseRef.current), type: 'mic_on' })
      setPuzzleIndex(index)
      setPhase('playing')
    },
    [pushEvent]
  )

  // ---- mode entry ----
  const beginPractice = useCallback(() => {
    track('labs_practice_start')
    setMode('practice')
    setConditionId(null)
    setArm(null)
    // stopRule is NOT reset here: it is a session-level assignment drawn at consent, so that the
    // Pass control is absent from practice too rather than appearing and then being taken away.
    sessionTracesRef.current = []
    setSessionPuzzles(practiceSession())
    startPuzzle(0)
  }, [startPuzzle])

  const beginTest = useCallback(() => {
    // Logged BEFORE any work that can fail, so a press that never produces a test is still
    // on the record. Do not move this below the arm draw. (See shared/labs/funnel.ts.)
    logFunnel('test-click', funnelCtx())
    firstTestPuzzleLoggedRef.current = false
    try {
      const chosen: Arm = ARM_OVERRIDE ?? (PHASE2_ENABLED ? pickArm() : 'full-set')
      // Read, never re-drawn: the rule was assigned at consent so that practice ran under it too.
      // Re-drawing here would mean a participant could practise under one rule and be tested under
      // another, which is the very inconsistency this arrangement removes.
      const rule: StopRule | null = stopRuleRef.current
      const { conditionId: cid, puzzles } = buildArmSession(chosen)
      track('labs_test_start', { condition_id: cid, arm: chosen, stop_rule: rule ?? 'none' })
      // eslint-disable-next-line no-console
      console.log(`[labs24] test start, arm=${chosen} stopRule=${rule} condition=${cid}`)
      setMode('test')
      setArm(chosen)
      setConditionId(cid)
      sessionTracesRef.current = []
      coreLenRef.current = puzzles.length
      extendedRef.current = false
      setSessionPuzzles(puzzles)
      startPuzzle(0)
      logFunnel('test-started', funnelCtx(), {
        conditionId: cid,
        arm: chosen,
        nPuzzles: puzzles.length,
        // startPuzzle(0) returns normally when the streaming cap is already hit, so a
        // test-started with no puzzle is expected here rather than a failure.
        cappedAtStart: overDailyCap(),
      })
    } catch (err) {
      logFunnel('test-start-failed', funnelCtx(), errDetail(err))
      // Re-thrown on purpose: this is an instrument, not a fix. Swallowing here would change
      // what the participant experiences and contaminate the measurement it is taken for.
      throw err
    }
  }, [startPuzzle, funnelCtx])

  const beginPlay = useCallback(
    (difficulty?: Difficulty) => {
      track('labs_play_start', { difficulty: difficulty || 'any' })
      setMode('play')
      setConditionId(null)
      setArm(null)
      // stopRule persists into free play too: RM, 2026-10-16, "remove the pass button from all
      // puzzles ever shown to a user in the no quit arm".
      setPlayDifficulty(difficulty)
      sessionTracesRef.current = []
      setSessionPuzzles([pickPlayPuzzle(difficulty)])
      startPuzzle(0)
    },
    [startPuzzle]
  )

  // ---- start / consent / mic ----
  const afterMic = useCallback(() => {
    if (overDailyCap()) setPhase('daily-limit')
    else if (isTestDone()) setPhase('mode-choice')
    else setPhase('practice-intro')
  }, [])

  const requestMic = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      stream.getTracks().forEach((t) => t.stop())
      track('labs_mic_result', { granted: 'yes' })
      afterMic()
    } catch {
      track('labs_mic_result', { granted: 'no' })
      setPhase('mic-error')
    }
  }, [afterMic])

  const handleStart = useCallback(async () => {
    markConsented() // latch the one-time labs consent (skips the screen on future visits/games)
    const id = uuidv4()
    const startedAtISO = new Date().toISOString()
    const env = captureEnv()
    sessionRef.current = { id, startedAtISO }
    envRef.current = env
    // The stopping-rule arm, for the WHOLE session: practice, test and free play alike. Drawn
    // here and nowhere else. Returning players who skip the consent screen reach this via the
    // auto-start effect below, so this is the single draw point for everyone.
    const rule: StopRule = STOPRULE_OVERRIDE ?? pickStopRule()
    stopRuleRef.current = rule
    setStopRule(rule)
    // Deliberately not dev-gated, per RM. There is otherwise no way to see the assignment at the
    // time: `track` posts to GA4 and fails silently, so the only feedback was a BigQuery query
    // after the fact, which cost an hour of QA on 2026-10-16 to a browser serving a stale bundle.
    // eslint-disable-next-line no-console
    console.log(
      `[labs24] session ${id.slice(0, 8)} stopRule=${rule}` +
        `${STOPRULE_OVERRIDE ? ' (?stop= override)' : ''} build=${LABS_BUILD}`
    )
    setLabsUser({ labs_session_id: id, labs_stt_engine: STT_ENGINE })
    // Installed once a session id exists, so a window error can be attributed to a session.
    installWindowErrorLogger(funnelCtx)
    track('labs_consent', { consented: 'yes' })
    const base = performance.now()
    tsBaseRef.current = base
    setTsBase(base)
    saveConsent({
      sessionId: id,
      consented: true,
      env,
      retroAfterN: Number.isFinite(RETRO.n) ? RETRO.n : null,
      retroCode: RETRO.code,
      sttEngine: STT_ENGINE,
      source: SOURCE,
      revealArm: REVEAL.arm,
      revealThreshold: REVEAL.threshold,
      stopRule: rule,
    }).catch(() => {})
    enrichCountry().then((c) => {
      if (c && envRef.current) envRef.current.country = c
    })
    await requestMic()
  }, [requestMic, funnelCtx])

  // Returning player who already consented (this device) → skip the consent screen and start.
  useEffect(() => {
    if (IN_APP) return // never auto-start in an app WebView — see BrowserHandoff
    if (hasConsented()) handleStart()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ---- reflection → auto-score ----
  const endReflection = useCallback(() => {
    if (!puzzle) return
    pushEvent({ ts: Math.round(performance.now() - tsBase), type: 'reflection_end' })
    pushEvent({ ts: Math.round(performance.now() - tsBase), type: 'mic_off' })
    reflectionEndRef.current = performance.now()
    const rawSpoken = transcriptRef.current
      .filter((t) => t.isFinal)
      .map((t) => t.text)
      .join(' ')
    const match = scoreTranscript(rawSpoken, puzzle.numbers)
    setAutoMatch(match)
    if (match.fired) {
      pushEvent({ ts: Math.round(performance.now() - tsBase), type: 'answer_detected' })
      setPhase('detected')
    } else {
      setPhase('entry')
    }
  }, [puzzle, tsBase, pushEvent])

  // ---- build + save one trace, then advance by mode ----
  const buildTrace = (answerMode: AnswerMode, typed: ReturnType<typeof judgeGame24> | null, typedRaw: string | null): PuzzleTrace => {
    const p = puzzle!
    const pStart = puzzleStartRef.current!
    const match = autoMatch
    return {
      schemaVersion: TRACE_SCHEMA_VERSION,
      sessionId: sessionRef.current!.id,
      mode,
      conditionId: mode === 'test' ? conditionId : null,
      arm: mode === 'test' ? arm : null,
      band: p.band ?? null,
      trialIndex: puzzleIndex,
      sitting: 1,
      resumed: false,
      aborted: false,
      puzzleId: p.id,
      numbers: p.numbers,
      difficulty: p.difficulty,
      decile: p.decile ?? null,
      humanRank: p.rank,
      humanSolveRate: p.solveRate,
      transcript: transcriptRef.current,
      events: eventsRef.current,
      committedTranscript: committedTranscriptRef.current,
      sttEngine: STT_ENGINE,
      exampleSolution: solutionExample,
      autoFired: match?.fired ?? false,
      autoMethod: match?.method ?? null,
      autoStars: match?.stars ?? 0,
      autoSimilarity: match?.similarity ?? 0,
      autoMatchedSolution: match?.matchedSolution ?? null,
      autoRejected: autoRejectedRef.current,
      answerMode,
      typedAnswer: typedRaw,
      typedValue: typed ? typed.value : null,
      typedReason: typed ? typed.reason : null,
      correct: answerMode === 'voice-detected' ? true : !!typed?.correct,
      correctnessReason: answerMode,
      puzzleStartedAt: pStart.iso,
      // Clamp at the 3-min limit: a puzzle can never legitimately run longer, so this stops a
      // backgrounded-tab overrun (suspended timer) from recording hours and skewing durations/spend.
      // A held trial ran on past the commit, so its duration is to the END of the forced
      // window. Without this every forced failure would record the moment they gave up.
      puzzleDurationMs: Math.min(
        Math.round((forcedEndRef.current || committedAtRef.current) - pStart.perf),
        PUZZLE_LIMIT_MS,
      ),
      reflectionDurationMs: Math.round(reflectionEndRef.current - committedAtRef.current),
      env: envRef.current!,
      consented: true,
      source: SOURCE,
      ...SKILL,
      revealArm: REVEAL.arm,
      revealRegime: REGIME,
      revealThreshold: REVEAL.threshold,
      revealForced: REVEAL.forced,
      // First exposure iff this device hasn't seen this puzzle before now (advance() marks it
      // seen just after). Read here so the flag reflects state *before* this attempt.
      firstExposure: !hasSeenPuzzle(p.numbers),
      labsBuild: LABS_BUILD,
      stopRule,
      forcedWindowMs:
        forcedStartRef.current && forcedEndRef.current
          ? Math.round(forcedEndRef.current - forcedStartRef.current)
          : null,
    }
  }

  // The mode-specific "what next" branch — run straight after a puzzle, or after its
  // retrospective follow-up, so the retro sits between a solved puzzle and the next.
  const finishAdvance = () => {
    if (mode === 'practice') {
      // Advance through the two distinct warm-up puzzles regardless of outcome (our
      // voice "solved" detection is fuzzier than Wurgaft's typed input, so looping on
      // the same puzzle just looked like a duplicate).
      if (puzzleIndex < sessionPuzzles.length - 1) startPuzzle(puzzleIndex + 1)
      else {
        setPhase('test-intro')
        logFunnel('test-intro-shown', funnelCtx(), { from: 'practice' })
      }
    } else if (mode === 'test') {
      // Capture the logged-in identity on the FIRST test puzzle (engagement, not
      // completion), so partial players are captured too. Deduped once per session.
      if (isLoggedIn() && sessionRef.current && !panelCapturedRef.current) {
        panelCapturedRef.current = true
        const idy = readNerdleIdentity()
        savePanelSignup({
          sessionId: sessionRef.current.id,
          method: 'logged-in',
          lblToken: idy?.lblToken ?? null,
          email: idy?.email ?? null,
        }).catch(() => {})
      }
      if (puzzleIndex < sessionPuzzles.length - 1) {
        startPuzzle(puzzleIndex + 1)
      } else {
        // End of the currently-loaded test set. Offer the opt-in extension once, if the
        // retro regime is active; otherwise wrap up.
        markTestDone()
        if (!extendedRef.current) {
          track('labs_test_complete', { solved_count: sessionTracesRef.current.filter((t) => t.correct).length })
        }
        // Extension ("keep going") is full-set only: the contrast arms are a fixed 5 + 5 draw, so
        // they wrap up after the core 10. (The per-puzzle retro follow-up still runs on all arms.)
        const retroActive = Number.isFinite(RETRO.n) && RETRO.n <= coreLenRef.current - 1
        if (arm === 'full-set' && retroActive && !extendedRef.current) {
          track('labs_continue_offer')
          setPhase('continue-offer')
        } else setPhase('publish-consent')
      }
    } else {
      // play: load another puzzle and keep going
      setSessionPuzzles((prev) => [...prev, pickPlayPuzzle(playDifficulty)])
      startPuzzle(puzzleIndex + 1)
    }
  }

  const advance = (answerMode: AnswerMode, typed: ReturnType<typeof judgeGame24> | null, typedRaw: string | null) => {
    const trace = buildTrace(answerMode, typed, typedRaw) // reads firstExposure before we mark it
    markPuzzleSeen(trace.numbers) // persistent seen-set → unseen-first extension + repeat flag
    sessionTracesRef.current = [...sessionTracesRef.current, trace]
    addStreamedMs(trace.puzzleDurationMs + trace.reflectionDurationMs) // mic-on time → daily cap
    savePuzzleTrace(trace)
      .then((r) => {
        // eslint-disable-next-line no-console
        console.log(`[trace] ${mode}${trace.arm ? `/${trace.arm}` : ''} #${trace.trialIndex} ${trace.puzzleId}${trace.band ? ` (${trace.band})` : ''} cond=${trace.conditionId ?? '-'} → ${r.collection}${r.fellBack ? ' (fallback)' : ''}`, r.id)
      })
      .catch((e: any) => {
        // eslint-disable-next-line no-console
        console.error('[trace] save failed', e)
        setSaveError(e?.message || 'save failed')
      })

    track('labs_puzzle_end', {
      mode,
      trial_index: puzzleIndex,
      outcome:
        trace.answerMode === 'voice-detected'
          ? 'solved_spoken'
          : trace.answerMode === 'timeout'
          ? 'timeout'
          : trace.answerMode === 'pass'
          ? 'pass'
          : trace.correct
          ? 'solved_typed'
          : 'typed_wrong',
      correct: trace.correct ? 'yes' : 'no',
      stt_engine: STT_ENGINE,
      spoke: (trace.committedTranscript || '').trim() ? 'yes' : 'no',
    })

    // Retrospective ("aha") follow-up: only on SOLVED test-mode puzzles once the regime is
    // active. The concurrent trace is already saved above, so a drop-out during the retro
    // never costs the (crown-jewel) trace.
    // Retro fires on solved puzzles: in TEST once the regime is active (trialIndex >= N),
    // and in free PLAY as an optional (skippable) extra now the core 10 are banked.
    const retroDue =
      trace.correct &&
      ((mode === 'test' && Number.isFinite(RETRO.n) && puzzleIndex >= RETRO.n) || mode === 'play')
    if (retroDue) {
      retroTranscriptRef.current = []
      setRetroSpoken('')
      setRetroInterim('')
      retroPromptedAtRef.current = performance.now()
      retroPromptedISORef.current = new Date().toISOString()
      setPhase('retro')
    } else {
      finishAdvance()
    }
  }

  // Retro answered/skipped → write the separate create-only retro doc, then advance.
  const handleRetroDone = (answer: RetroAnswer) => {
    const p = puzzle
    if (p && sessionRef.current) {
      const retro: PuzzleRetro = {
        schemaVersion: RETRO_SCHEMA_VERSION,
        sessionId: sessionRef.current.id,
        trialIndex: puzzleIndex,
        puzzleId: p.id,
        numbers: p.numbers,
        solveFelt: answer.solveFelt,
        changedTranscript: retroTranscriptRef.current.length ? retroTranscriptRef.current : null,
        changedText: answer.changedText,
        sttEngine: STT_ENGINE,
        skipped: answer.skipped,
        retroAfterN: Number.isFinite(RETRO.n) ? RETRO.n : null,
        retroCode: RETRO.code,
        promptedAt: retroPromptedISORef.current,
        answerDurationMs: Math.round(performance.now() - retroPromptedAtRef.current),
      }
      addStreamedMs(retro.answerDurationMs) // retro mic-on time → daily cap
      savePuzzleRetro(retro)
        .then((r) => {
          // eslint-disable-next-line no-console
          console.log(`[retro] #${retro.trialIndex} → ${r.collection}${r.fellBack ? ' (fallback)' : ''}`, r.id)
        })
        .catch((e: any) => {
          // eslint-disable-next-line no-console
          console.error('[retro] save failed', e)
        })
    }
    track('labs_retro', { solve_felt: answer.solveFelt || 'none', skipped: answer.skipped ? 'yes' : 'no' })
    finishAdvance()
  }

  // Opt-in extension: E more research puzzles, each retro'd (their index >= any active N).
  const keepGoingExtension = () => {
    track('labs_continue', { accepted: 'yes' })
    extendedRef.current = true
    // Extension draws from Wurgaft-200 problems this user hasn't seen (the core 10 just played
    // are already in the seen-set), so continuers accumulate more coverage of the same set.
    setSessionPuzzles((prev) => [...prev, ...buildExtensionPuzzles(EXTENSION_PUZZLES, seenPuzzleKeys())])
    startPuzzle(puzzleIndex + 1)
  }

  // End an unsolved puzzle — voluntary give-up ('pass') or the 3-minute limit ('timeout').
  // Snapshots the transcript so far (what they said while failing), records no answer, and
  // advances. Guarded against double-fire so a timeout can't race a give-up.
  const endUnsolved = (reason: 'pass' | 'timeout') => {
    if (hasCommittedRef.current) return
    hasCommittedRef.current = true
    setShowPassConfirm(false)
    const segments: ConfirmedSegment[] = transcriptRef.current
      .filter((t) => t.isFinal)
      .map((t) => ({ text: t.text.trim(), ts: t.ts }))
      .filter((s) => s.text)
    committedTranscriptRef.current = formatWithPauses(segments)
    const now = performance.now()
    committedAtRef.current = now
    reflectionEndRef.current = now
    pushEvent({ ts: Math.round(now - tsBase), type: reason === 'timeout' ? 'timeout' : 'answer_passed' })
    pushEvent({ ts: Math.round(now - tsBase), type: 'mic_off' })
    setAutoMatch(null)
    advance(reason, null, null)
  }
  endUnsolvedRef.current = endUnsolved

  const acceptDetected = () => advance('voice-detected', null, null)
  const rejectDetected = () => {
    autoRejectedRef.current = true
    pushEvent({ ts: Math.round(performance.now() - tsBase), type: 'answer_detection_rejected' })
    setPhase('entry')
  }
  // ---- the FORCED arm's held window ----
  // Entered instead of ending a failed trial. The pending outcome is what the trial would have
  // recorded, and it is what gets recorded unless the detector fires inside the window.
  const enterForcedWindow = (mode: AnswerMode, typed: ReturnType<typeof judgeGame24> | null, raw: string | null) => {
    pendingRef.current = { mode, typed, raw }
    forcedStartRef.current = performance.now()
    pushEvent({ ts: Math.round(performance.now() - tsBase), type: 'forced_window_start' })
    setPhase('forced')
  }
  const endForcedWindow = (detected: boolean) => {
    const p = pendingRef.current
    if (!p) return
    pendingRef.current = null
    forcedEndRef.current = performance.now()
    // Re-snapshot the committed transcript so the held window's speech reaches the analysis:
    // `spoken_text` prefers `committedTranscript`, which was frozen at the commit, and without
    // this the arm's whole point would be excluded from every word count and marker rate.
    // `forced_window_start` stays in the events as the boundary between the two halves.
    {
      const segs: ConfirmedSegment[] = transcriptRef.current
        .filter((x) => x.isFinal)
        .map((x) => ({ text: x.text.trim(), ts: x.ts }))
        .filter((s) => s.text)
      committedTranscriptRef.current = formatWithPauses(segs)
    }
    pushEvent({ ts: Math.round(performance.now() - tsBase), type: 'forced_window_end' })
    if (detected) {
      pushEvent({ ts: Math.round(performance.now() - tsBase), type: 'answer_detected' })
      advance('voice-detected', null, null)
    } else {
      advance(p.mode, p.typed, p.raw)
    }
  }
  endForcedWindowRef.current = endForcedWindow

  // The detector runs on every new final segment while the window is open, so a participant who
  // says a correct answer gets out rather than sitting to the cap. `scoreTranscript` is local
  // string work, so re-running it per segment costs nothing. Rejection is impossible here (the
  // commit is spent), so this is an UNCONFIRMED match: measured at 0.4% player-rejection and zero
  // false positives across 978 fires, and flagged on the trace by `autoRejected` staying false.
  useEffect(() => {
    if (phase !== 'forced' || !puzzle) return
    const raw = transcriptRef.current.filter((x) => x.isFinal).map((x) => x.text).join(' ')
    const match = scoreTranscript(raw, puzzle.numbers)
    if (match.fired) {
      setAutoMatch(match)
      endForcedWindowRef.current(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [confirmedSegments, phase, puzzle])

  const handlePass = () => {
    pushEvent({ ts: Math.round(performance.now() - tsBase), type: 'answer_passed' })
    // Forced arm: declaring no answer no longer ENDS the trial, it opens the held window. The
    // control stays visible in both arms, because hiding it would leave a participant who
    // committed with nothing to enter stuck on this screen with no way forward but typing a
    // deliberately wrong expression, which would pollute the wrong-commit category.
    if (stopRule === 'forced') {
      enterForcedWindow('pass', null, null)
      return
    }
    advance('pass', null, null)
  }
  const submitTyped = (expr: string) => {
    if (!puzzle) return
    const verdict = judgeGame24(expr, puzzle.numbers)
    lastTypedRef.current = expr
    setTypedResult(verdict)
    pushEvent({ ts: Math.round(performance.now() - tsBase), type: 'answer_typed', value: expr })
    // Forced arm, wrong answer: the trial is not over, it runs to the limit. A CORRECT answer
    // ends it here exactly as in the free arm, because only unsolved trials are held.
    if (stopRule === 'forced' && !verdict.correct) {
      enterForcedWindow('typed', verdict, expr)
      return
    }
    setPhase('verdict')
  }

  // Label for the result-screen Continue button, by mode.
  const continueLabel =
    mode === 'test' ? (isLastInSession ? 'See your results' : 'Next puzzle') : mode === 'practice' ? 'Continue' : 'Next puzzle'

  // ---- render ----
  const renderScreen = () => {
    if (IN_APP) return <BrowserHandoff game="twentyfour" />
    if (STT_ENGINE === 'webspeech' && gateReason !== 'ok') return <BrowserGate reason={gateReason} game="twentyfour" />

    switch (phase) {
      case 'consent':
        if (hasConsented()) return null // auto-starting via the mount effect
        return <ConsentScreen onConsent={handleStart} />

      case 'mic-error':
        return (
          <StatusScreen
            game="twentyfour"
            emoji="🚫"
            title="We couldn’t access your mic"
            body="The 24-game needs microphone access. Check your browser’s mic permission for this site, then try again."
            action={{ label: 'Try again', onClick: requestMic }}
          />
        )

      case 'daily-limit':
        return (
          <StatusScreen
            game="twentyfour"
            emoji="✋"
            title="That’s your play for today"
            body="You’ve reached today’s voice-play limit — it resets tomorrow. Thanks for helping our research!"
          />
        )

      case 'mode-choice':
        return (
          <div className="flex flex-col items-center justify-center px-6 pt-16 pb-8 text-center">
            <Brand game="twentyfour" />
            <h2 className="mt-8 text-xl font-semibold text-gray-800">Welcome back</h2>
            <p className="mt-2 max-w-sm text-gray-600">You’ve already completed the 10-game test. Jump into free play, or take the test again.</p>
            <button type="button" onClick={() => beginPlay(undefined)} className="mt-6 w-full max-w-xs rounded-lg bg-[#820458] py-3 text-white font-semibold">
              Free play
            </button>
            <button type="button" onClick={beginTest} className="mt-3 text-sm text-gray-500 underline">
              Take the 10-game test again
            </button>
            <div className="w-full max-w-xs">
              <OtherGamesLinks current="twentyfour" />
            </div>
          </div>
        )

      case 'practice-intro':
        return (
          <StatusScreen
            game="twentyfour"
            emoji="👋"
            title="Quick warm-up"
            body="Before the main test, two quick practice puzzles to get used to thinking out loud — up to 3 minutes each. Solve each one to continue."
            action={{ label: 'Start warm-up', onClick: beginPractice }}
          />
        )

      case 'test-intro':
        return (
          <StatusScreen
            game="twentyfour"
            emoji="🎯"
            title="The 10-game test"
            body={
              <>
                Now the real thing: 10 puzzles across the full difficulty range.{' '}
                <strong className="font-semibold text-gray-800">
                  You have up to 3 minutes to attempt each puzzle.
                </strong>{' '}
                Think out loud as you solve — finishing all 10 unlocks unlimited free play.
              </>
            }
            action={{ label: 'Start the test', onClick: beginTest }}
          />
        )

      case 'play-intro':
        return (
          <div className="flex flex-col items-center justify-center px-6 pt-16 pb-8 text-center">
            <Brand game="twentyfour" />
            <h2 className="mt-8 text-xl font-semibold text-gray-800">Free play</h2>
            <p className="mt-2 max-w-sm text-gray-600">Pick a difficulty and keep going as long as you like.</p>
            <div className="mt-6 grid w-full max-w-xs grid-cols-2 gap-3">
              {(['easy', 'medium', 'hard'] as Difficulty[]).map((d) => (
                <button key={d} type="button" onClick={() => beginPlay(d)} className="rounded-lg bg-gray-100 py-3 font-semibold capitalize text-gray-700 hover:bg-gray-200">
                  {d}
                </button>
              ))}
              <button type="button" onClick={() => beginPlay(undefined)} className="rounded-lg bg-[#820458] py-3 font-semibold text-white">
                Any
              </button>
            </div>
          </div>
        )

      case 'playing': {
        if (!puzzle) return null
        const showNudge = performance.now() - lastActivityRef.current > NUDGE_SILENCE_MS
        return (
          <div key={puzzleIndex} className="labs-puzzle-fade">
            <PuzzleView
              numbers={puzzle.numbers}
              energy={energy.energy}
              speaking={energy.speaking}
              dimmed={energy.dimmed}
              confirmedSegments={confirmedSegments}
              interimText={interimText}
              progressLabel={progressLabel}
              showNudge={showNudge}
              onCommit={handleCommit}
              onPass={
                stopRule === 'forced'
                  ? undefined
                  : () => {
                      pushEvent({ ts: Math.round(performance.now() - tsBase), type: 'pass_prompt' })
                      setShowPassConfirm(true)
                    }
              }
            />
            {showPassConfirm && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-6">
                <div className="w-full max-w-xs rounded-xl bg-white p-5 text-center shadow-xl">
                  <div className="text-2xl">⏳</div>
                  <h3 className="mt-2 font-semibold text-gray-800">Give up on this one?</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    You’ve got up to 3 minutes — talking it through a little longer often cracks it. Keep going?
                  </p>
                  <button
                    type="button"
                    onClick={() => setShowPassConfirm(false)}
                    className="mt-4 w-full rounded-lg bg-[#398874] py-3 font-semibold text-white"
                  >
                    Keep going
                  </button>
                  <button
                    type="button"
                    onClick={() => endUnsolved('pass')}
                    className="mt-2 w-full rounded-lg border border-gray-200 py-2.5 text-sm font-medium text-gray-500 hover:bg-gray-50"
                  >
                    Give up
                  </button>
                  {mode === 'play' && (
                    <button
                      type="button"
                      onClick={() => { window.location.href = labsHomeUrl() }}
                      className="mt-3 block w-full text-xs text-gray-400 underline"
                    >
                      Back to other games
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        )
      }

      case 'reflection':
        if (!puzzle) return null
        return (
          <PuzzleStage numbers={puzzle.numbers} confirmedSegments={confirmedSegments} interimText={interimText} progressLabel={progressLabel}>
            <ReflectionNotice seconds={REFLECTION_SECONDS} onDone={endReflection} />
          </PuzzleStage>
        )

      case 'detected':
        if (!puzzle) return null
        return (
          <PuzzleStage numbers={puzzle.numbers} confirmedSegments={confirmedSegments} interimText={interimText} progressLabel={progressLabel}>
            <div className="text-center">
              <div className="text-3xl">🎉</div>
              <div className="mt-1 font-semibold text-gray-800">Sounds like you got it!</div>
              {autoMatch?.matchedSolution && (
                <div className="mt-2 inline-block rounded-lg bg-[#398874]/10 px-4 py-2 font-semibold text-[#398874]">
                  {prettyExpr(autoMatch.matchedSolution)} = 24
                </div>
              )}
              <button type="button" onClick={acceptDetected} className="mt-4 w-full rounded-lg bg-[#820458] py-3 text-white font-semibold">
                {continueLabel}
              </button>
              <button type="button" onClick={rejectDetected} className="mt-3 text-sm text-gray-500 underline">
                That wasn’t my answer — let me type it
              </button>
              {mode === 'play' && (
                <>
                  <OtherGamesLinks current="twentyfour" />
                  <button type="button" onClick={() => setPhase('play-intro')} className="mt-3 block w-full text-xs text-gray-400 underline">
                    Done playing
                  </button>
                </>
              )}
            </div>
          </PuzzleStage>
        )

      case 'entry':
        if (!puzzle) return null
        return (
          <PuzzleStage numbers={puzzle.numbers} confirmedSegments={confirmedSegments} interimText={interimText} progressLabel={progressLabel}>
            <AnswerKeypad numbers={puzzle.numbers} onSubmit={submitTyped} onPass={handlePass} />
          </PuzzleStage>
        )

      case 'forced': {
        // The forced arm's held window. No commit control: the participant has spent their one
        // commit, so the only way out is the clock or a correct answer the detector hears. The
        // copy must not direct them to EXPLAIN anything: the paper's non-reactivity claim rests
        // on Fox and Ericsson, where merely verbalising leaves performance unchanged while being
        // directed to explain reasoning improves it. "Think out loud" only.
        if (!puzzle) return null
        return (
          <PuzzleStage
            numbers={puzzle.numbers}
            confirmedSegments={confirmedSegments}
            interimText={interimText}
            progressLabel={progressLabel}
            showNudge={performance.now() - lastActivityRef.current > NUDGE_SILENCE_MS}
          >
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-center">
              <h3 className="font-semibold text-gray-800">Keep going on this one</h3>
              <p className="mt-2 text-sm text-gray-600">
                There&rsquo;s still time on the clock. Think out loud as you keep looking, and if you
                say something that works we&rsquo;ll <span className="font-medium">try</span> to pick
                it up.
              </p>
            </div>
            <p className="mt-2 text-center text-xs text-gray-400">Up to 3 minutes per puzzle.</p>
          </PuzzleStage>
        )
      }

      case 'verdict': {
        if (!puzzle) return null
        const note = verdictNote(typedResult, puzzle.numbers)
        const exprLine = lastTypedRef.current
          ? `${prettyExpr(lastTypedRef.current)}${typedResult && typedResult.value !== null ? ` = ${typedResult.value}` : ''}`
          : null
        const wrong = typedResult ? !typedResult.correct : false
        return (
          <PuzzleStage numbers={puzzle.numbers} confirmedSegments={confirmedSegments} interimText={interimText} progressLabel={progressLabel}>
            <div className="text-center">
              <div className="text-3xl">{typedResult?.correct ? '✅' : '🤔'}</div>
              <div className="mt-1 font-semibold text-gray-800">{typedResult?.correct ? 'Correct — that’s 24!' : 'Not quite'}</div>
              {note && <p className="mt-1 text-sm text-gray-500">{note}</p>}
              {exprLine && (
                <div className={`mt-3 inline-block rounded-lg px-4 py-2 font-semibold ${wrong ? 'bg-gray-100 text-gray-700' : 'bg-[#398874]/10 text-[#398874]'}`}>
                  {exprLine}
                </div>
              )}
              {wrong && solutionExample && (
                <div className="mt-3 rounded-lg bg-gray-50 px-4 py-3">
                  <div className="text-xs text-gray-400">One way to make 24</div>
                  <div className="mt-1 font-semibold text-gray-800">{prettyExpr(solutionExample)} = 24</div>
                </div>
              )}
              <button type="button" onClick={() => advance('typed', typedResult, lastTypedRef.current)} className="mt-5 w-full rounded-lg bg-[#820458] py-3 text-white font-semibold">
                {continueLabel}
              </button>
              {mode === 'play' && (
                <>
                  <OtherGamesLinks current="twentyfour" />
                  <button type="button" onClick={() => setPhase('play-intro')} className="mt-3 block w-full text-xs text-gray-400 underline">
                    Done playing
                  </button>
                </>
              )}
            </div>
          </PuzzleStage>
        )
      }

      case 'retro':
        if (!puzzle) return null
        return (
          <RetroScreen
            key={puzzleIndex}
            numbers={puzzle.numbers}
            committedTranscript={committedTranscriptRef.current}
            spokenAnswer={retroSpoken}
            interimAnswer={retroInterim}
            commitSignal={retroCommitSignal}
            onDone={handleRetroDone}
          />
        )

      case 'continue-offer':
        return (
          <div className="flex flex-col items-center justify-center px-6 pt-16 pb-8 text-center">
            <Brand game="twentyfour" />
            <div className="mt-8 text-3xl">🙌</div>
            <h2 className="mt-3 text-xl font-semibold text-gray-800">That’s the test done — thank you!</h2>
            <p className="mt-2 max-w-sm text-gray-600">
              Fancy helping the research with a few more puzzles? Each has a quick reflection afterwards.
            </p>
            <button type="button" onClick={keepGoingExtension} className="mt-6 w-full max-w-xs rounded-lg bg-[#820458] py-3 font-semibold text-white">
              Keep going
            </button>
            <button
              type="button"
              onClick={() => {
                track('labs_continue', { accepted: 'no' })
                setPhase('publish-consent')
              }}
              className="mt-3 text-sm text-gray-500 underline"
            >
              See my results
            </button>
          </div>
        )

      case 'summary': {
        const surveyPending = !!sessionRef.current && !surveyCompletedAt(labsFeedbackSurvey.id)
        return (
          <SummaryScreen
            plays={sessionTracesRef.current}
            saveError={saveError}
            action={{ label: '🔓 Unlock unlimited free play', onClick: () => setPhase('play-intro') }}
            feedback={
              surveyPending
                ? {
                    onGive: () => {
                      track('labs_survey_view')
                      setPhase('survey')
                    },
                    onSkip: () => {
                      markSurveyDone(labsFeedbackSurvey.id)
                      afterFeedback()
                    },
                  }
                : undefined
            }
          />
        )
      }

      case 'publish-consent':
        if (!sessionRef.current) return null
        return (
          <PublishConsent
            sessionId={sessionRef.current.id}
            gameType="labs-24"
            onDone={() => setPhase('summary')}
          />
        )

      case 'survey':
        if (!sessionRef.current) return null
        return (
          <SurveyScreen
            schema={labsFeedbackSurvey}
            sessionId={sessionRef.current.id}
            onDone={() => afterFeedback()}
          />
        )

      case 'panel':
        if (!sessionRef.current) return null
        return (
          <PanelSignup
            sessionId={sessionRef.current.id}
            onDone={() => {
              panelDoneRef.current = true
              setPhase('play-intro')
            }}
          />
        )

      default:
        return null
    }
  }

  return <AdLayout debugInfo={{ audioSeconds: streaming.audioSeconds }}>{renderScreen()}</AdLayout>
}

export default Game24
